/**
 * Widget picker modal — two tabs:
 *   1. Presets: curated starter widgets that answer specific questions
 *   2. Custom: build any metric × dimension × chart type combination
 */
import { useMemo, useState, useEffect } from 'react'
import { Plus, X, BarChart3, TrendingUp, Grid3x3, Table2, Sparkles, Wrench } from 'lucide-react'
import { execAPI } from '../../services/exec'
import { useExecDashboard } from '../../contexts/ExecDashboardContext'
import { getPresetsByCategory, type WidgetPreset } from './widget-presets'
import {
  METRIC_LABELS, DIMENSION_LABELS, GEO_DIMENSIONS,
  type MetricKey, type GroupByDimension, type ChartType,
} from './generic/types'

/**
 * Derive the measure key for a Custom-tab widget from its config.
 * Previously the picker always saved `'office_performance'` regardless of config,
 * so a state/CD-grouped widget could never use the geo endpoint and would
 * silently route to the wrong API. This maps from the chosen `group_by` and
 * `metric` to the appropriate `measure` key in the MEASURES registry so
 * `usePerformanceQuery` picks the right endpoint and `MeasureWidget` routes
 * correctly.
 */
function deriveMeasureFromConfig(groupBy: GroupByDimension, metric: MetricKey): string {
  if (GEO_DIMENSIONS.has(groupBy)) return 'geo_performance'
  // Competition-flavored metrics → competition_health
  if (
    metric === 'full_open_pct' || metric === 'not_competed_pct' ||
    metric === 'avg_offers_received' || metric === 'single_offer_pct'
  ) return 'competition_health'
  // Small-business metrics → small_biz_engagement
  if (
    metric === 'sb_dollar_pct' || metric === 'sb_award_pct' || metric === 'set_aside_pct' ||
    metric === 'sb_awards' || metric === 'sb_dollars' ||
    metric === 'count_8a' || metric === 'count_hubzone' || metric === 'count_sdvosb' || metric === 'count_wosb'
  ) return 'small_biz_engagement'
  // Volume metrics + default → office_performance
  return 'office_performance'
}
import type { WidgetConfig, PortfolioGroup } from '../../types/exec'

interface Props {
  open: boolean
  onClose: () => void
  onAdd: (measure: string, config?: WidgetConfig, title?: string, gridW?: number, gridH?: number) => void
}

type Tab = 'presets' | 'custom'

export default function WidgetPicker({ open, onClose, onAdd }: Props) {
  const [tab, setTab] = useState<Tab>('presets')
  const [search, setSearch] = useState('')

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Add Widget
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 px-4">
          <button
            onClick={() => setTab('presets')}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === 'presets'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Sparkles size={14} />
            Presets
          </button>
          <button
            onClick={() => setTab('custom')}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === 'custom'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Wrench size={14} />
            Custom
          </button>
        </div>

        {/* Search (presets only) */}
        {tab === 'presets' && (
          <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-800">
            <input
              type="text"
              placeholder="Search presets..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {tab === 'presets' ? (
            <PresetsTab search={search} onAdd={onAdd} onClose={onClose} />
          ) : (
            <CustomTab onAdd={onAdd} onClose={onClose} />
          )}
        </div>
      </div>
    </div>
  )
}


// =========================================================================
// Presets Tab
// =========================================================================

function PresetsTab({
  search, onAdd, onClose,
}: {
  search: string
  onAdd: Props['onAdd']
  onClose: () => void
}) {
  const { presetScope } = useExecDashboard()
  const grouped = useMemo(() => {
    const byCategory = getPresetsByCategory(presetScope)
    if (!search) return byCategory

    const filtered: Record<string, WidgetPreset[]> = {}
    const q = search.toLowerCase()
    for (const [cat, presets] of Object.entries(byCategory)) {
      const matching = presets.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q) ||
          cat.toLowerCase().includes(q),
      )
      if (matching.length > 0) filtered[cat] = matching
    }
    return filtered
  }, [search, presetScope])

  const chartIcon = (type?: string) => {
    switch (type) {
      case 'line': return <TrendingUp size={12} />
      case 'heatmap': return <Grid3x3 size={12} />
      case 'table': return <Table2 size={12} />
      default: return <BarChart3 size={12} />
    }
  }

  return (
    <div className="px-4 py-2">
      {Object.keys(grouped).length === 0 && (
        <p className="text-sm text-gray-400 py-8 text-center">No presets match your search.</p>
      )}

      {Object.entries(grouped)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([category, presets]) => (
          <div key={category} className="mb-4">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5 px-1">
              {category}
            </h3>
            <div className="space-y-0.5">
              {presets.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => {
                    onAdd(
                      preset.measure,
                      preset.config as WidgetConfig,
                      preset.title,
                      preset.gridW,
                      preset.gridH,
                    )
                    onClose()
                  }}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-gray-400">{chartIcon(preset.config.chart_type)}</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {preset.title}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 pl-5">
                      {preset.description}
                    </div>
                  </div>
                  <Plus size={16} className="text-gray-400 ml-2 shrink-0" />
                </button>
              ))}
            </div>
          </div>
        ))}
    </div>
  )
}


// =========================================================================
// Custom Builder Tab
// =========================================================================

const METRIC_OPTIONS: { value: MetricKey; label: string; group: string }[] = [
  { value: 'award_count', label: 'Award Count (actions)', group: 'Volume' },
  { value: 'unique_award_count', label: 'Distinct Contracts', group: 'Volume' },
  { value: 'total_obligated', label: 'Obligated Dollars', group: 'Volume' },
  { value: 'full_open_pct', label: 'Full & Open %', group: 'Competition' },
  { value: 'follow_on_pct', label: 'Follow-On %', group: 'Competition' },
  { value: 'ot_award_pct', label: 'Other Transaction %', group: 'Vehicles' },
  { value: 'deobligation_pct', label: 'Deobligation Rate', group: 'Volume' },
  { value: 'gross_obligated', label: 'Gross Obligated $', group: 'Volume' },
  { value: 'not_competed_pct', label: 'Not Competed %', group: 'Competition' },
  { value: 'single_offer_pct', label: 'Single Offer %', group: 'Competition' },
  { value: 'avg_offers_received', label: 'Avg Offers Received', group: 'Competition' },
  { value: 'sb_award_pct', label: 'SB Award %', group: 'Small Business' },
  { value: 'set_aside_pct', label: 'Set-Aside %', group: 'Small Business' },
  { value: 'count_8a', label: '8(a) Awards', group: 'Small Business' },
  { value: 'count_hubzone', label: 'HUBZone Awards', group: 'Small Business' },
  { value: 'count_sdvosb', label: 'SDVOSB Awards', group: 'Small Business' },
  { value: 'count_wosb', label: 'WOSB Awards', group: 'Small Business' },
]

const GROUP_BY_OPTIONS: { value: GroupByDimension; label: string }[] = [
  { value: 'fiscal_year', label: 'Fiscal Year' },
  { value: 'fiscal_quarter', label: 'Fiscal Quarter' },
  { value: 'sub_agency', label: 'Branch / Sub-Agency' },
  { value: 'macom', label: 'Major Command (MAJCOM)' },
  { value: 'subcom1', label: 'Sub-Command' },
  { value: 'awarding_office_code', label: 'Office' },
  { value: 'vehicle_category', label: 'Vehicle Category' },
  { value: 'psc_level1_code', label: 'PSC Category (Vertical)' },
  { value: 'color_of_money', label: 'Color of Money' },
  { value: 'extent_competed', label: 'Competition Status' },
  { value: 'is_ot', label: 'FAR vs OT' },
]

const CHART_TYPES: { value: ChartType; icon: React.ReactNode; label: string }[] = [
  { value: 'bar', icon: <BarChart3 size={16} />, label: 'Bar' },
  { value: 'line', icon: <TrendingUp size={16} />, label: 'Line' },
  { value: 'heatmap', icon: <Grid3x3 size={16} />, label: 'Heatmap' },
  { value: 'table', icon: <Table2 size={16} />, label: 'Table' },
]

function CustomTab({ onAdd, onClose }: { onAdd: Props['onAdd']; onClose: () => void }) {
  const [title, setTitle] = useState('')
  const [metric, setMetric] = useState<MetricKey>('award_count')
  const [groupBy, setGroupBy] = useState<GroupByDimension>('fiscal_year')
  const [seriesBy, setSeriesBy] = useState<GroupByDimension | ''>('')
  const [chartType, setChartType] = useState<ChartType>('bar')

  // Scope filter
  const [portfolioGroupId, setPortfolioGroupId] = useState('')

  // Load portfolio groups for the dropdown
  const [portfolioGroups, setPortfolioGroups] = useState<PortfolioGroup[]>([])
  useEffect(() => {
    execAPI.getPortfolioGroups().then(setPortfolioGroups).catch(() => {})
  }, [])

  const handleAdd = () => {
    const config: WidgetConfig = {
      group_by: groupBy,
      metrics: [metric],
      chart_type: chartType,
      ...(seriesBy ? { series_by: seriesBy } : {}),
      ...(portfolioGroupId ? { portfolio_group: portfolioGroupId } : {}),
    }

    const displayTitle = title || `${METRIC_LABELS[metric]} by ${DIMENSION_LABELS[groupBy]}`
    const isWide = chartType === 'heatmap' || chartType === 'table'
    const measure = deriveMeasureFromConfig(groupBy, metric)

    onAdd(measure, config, displayTitle, isWide ? 12 : 6, chartType === 'heatmap' ? 7 : 5)
    onClose()
  }

  const selectClass = 'w-full px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500'

  return (
    <div className="px-4 py-4 space-y-4">
      {/* Title — placeholder shows the live auto-generated title (still editable). */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Widget Title (optional)</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={`${METRIC_LABELS[metric]} by ${DIMENSION_LABELS[groupBy]}${
            seriesBy ? ` × ${DIMENSION_LABELS[seriesBy as GroupByDimension]}` : ''
          }`}
          className={selectClass}
        />
      </div>

      {/* Metric */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Metric (Y axis / value)</label>
        <select value={metric} onChange={(e) => setMetric(e.target.value as MetricKey)} className={selectClass}>
          {(() => {
            let lastGroup = ''
            return METRIC_OPTIONS.map((opt) => {
              const showGroup = opt.group !== lastGroup
              lastGroup = opt.group
              return (
                <option key={opt.value} value={opt.value}>
                  {showGroup ? `${opt.group}: ` : '  '}{opt.label}
                </option>
              )
            })
          })()}
        </select>
      </div>

      {/* Group By */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Group By (X axis / rows)</label>
        <select value={groupBy} onChange={(e) => setGroupBy(e.target.value as GroupByDimension)} className={selectClass}>
          {GROUP_BY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Compare By (series) */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Compare By (optional — creates grouped bars or multi-line)</label>
        <select value={seriesBy} onChange={(e) => setSeriesBy(e.target.value as GroupByDimension | '')} className={selectClass}>
          <option value="">None</option>
          {GROUP_BY_OPTIONS.filter((o) => o.value !== groupBy).map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Chart Type */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Chart Type</label>
        <div className="flex gap-2">
          {CHART_TYPES.map((ct) => (
            <button
              key={ct.value}
              onClick={() => setChartType(ct.value)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border transition-colors ${
                chartType === ct.value
                  ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                  : 'border-gray-200 dark:border-gray-700 text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              {ct.icon}
              {ct.label}
            </button>
          ))}
        </div>
      </div>

      {/* Scope Filter */}
      <div className="border-t border-gray-100 dark:border-gray-800 pt-4">
        <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
          Scope (optional)
        </label>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Portfolio Group</label>
          <select
            value={portfolioGroupId}
            onChange={(e) => setPortfolioGroupId(e.target.value)}
            className={selectClass}
          >
            <option value="">All offices (no scope filter)</option>
            {portfolioGroups.map((pg) => (
              <option key={pg.id} value={pg.id}>
                {pg.name} ({pg.resolved_office_count} offices)
              </option>
            ))}
          </select>
          <p className="text-[10px] text-gray-400 mt-1">
            <a href="/exec/portfolio-groups" className="text-blue-500 hover:underline">Manage portfolio groups</a>
          </p>
        </div>
      </div>

      {/* Add Button */}
      <div className="flex justify-end pt-2">
        <button
          onClick={handleAdd}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
        >
          <Plus size={16} />
          Add Widget
        </button>
      </div>
    </div>
  )
}
